import { entropyToMnemonic, mnemonicToSeedWebcrypto, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { randomBytes } from './bytes';

export const WORDLIST: readonly string[] = wordlist;
const INDEX = new Map(wordlist.map((w, i) => [w, i]));

export type MnemonicError =
  | { kind: 'invalidWordCount'; count: number }
  | { kind: 'unknownWord'; word: string }
  | { kind: 'invalidChecksum' };

export class InvalidMnemonic extends Error {
  constructor(public readonly reason: MnemonicError) {
    super('invalid mnemonic');
  }
}

/** Normalises what the user typed: lowercase, single spaces. */
export function normalizeWords(phrase: string): string[] {
  return phrase.toLowerCase().split(/\s+/).filter(Boolean);
}

/** Validates a phrase and returns it in canonical form, or throws InvalidMnemonic. */
export function checkMnemonic(phrase: string): string {
  const words = normalizeWords(phrase);
  if (![12, 15, 18, 21, 24].includes(words.length)) {
    throw new InvalidMnemonic({ kind: 'invalidWordCount', count: words.length });
  }
  for (const w of words) {
    if (!INDEX.has(w)) throw new InvalidMnemonic({ kind: 'unknownWord', word: w });
  }
  const canonical = words.join(' ');
  if (!validateMnemonic(canonical, wordlist)) throw new InvalidMnemonic({ kind: 'invalidChecksum' });
  return canonical;
}

/** New 12-word phrase from the browser's cryptographic random generator. */
export function generateMnemonic(): string {
  return entropyToMnemonic(randomBytes(16), wordlist);
}

export function mnemonicFromEntropy(entropy: Uint8Array): string {
  return entropyToMnemonic(entropy, wordlist);
}

export function seedFromMnemonic(phrase: string, passphrase = ''): Promise<Uint8Array> {
  return mnemonicToSeedWebcrypto(phrase, passphrase);
}

/** Up to `limit` words of the list starting with `prefix`. */
export function suggestions(prefix: string, limit = 4): string[] {
  const p = prefix.trim().toLowerCase();
  if (!p) return [];
  const out: string[] = [];
  for (const w of wordlist) {
    if (w.startsWith(p)) {
      out.push(w);
      if (out.length === limit) break;
    }
  }
  return out;
}

export function isWord(w: string): boolean {
  return INDEX.has(w);
}
